import { formatMoney } from '@/shared/utils/money';
import type { PortalOverview } from '../types/portalOverview';

export function formatPortalDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatLatestPaymentSummary(
  overview: PortalOverview,
  locale: string,
  formatStatus: (status: string) => string,
): string | null {
  if (!overview.last_payment_status) return null;
  const parts: string[] = [formatStatus(overview.last_payment_status)];
  if (overview.last_payment_amount !== null && overview.last_payment_currency) {
    parts.push(formatMoney(overview.last_payment_amount, overview.last_payment_currency, locale));
  }
  if (overview.last_payment_provider_label) {
    parts.push(overview.last_payment_provider_label);
  }
  return parts.join(' · ');
}

/** Elapsed share of the current billing or trial window (0–100), or null if unknown. */
export function portalPeriodProgressPercent(overview: PortalOverview): number | null {
  const isTrialing = overview.subscription_status === 'trialing' && overview.trial_ends_at !== null;
  const endIso = isTrialing ? overview.trial_ends_at : overview.current_period_end;
  const startIso = isTrialing
    ? (overview.started_at ?? overview.current_period_start)
    : (overview.current_period_start ?? overview.started_at);
  if (!startIso || !endIso) return null;

  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

  const now = Date.now();
  const elapsed = Math.min(Math.max(now - start, 0), end - start);
  return Math.round((elapsed / (end - start)) * 100);
}

export function portalDaysBadgeKey(overview: PortalOverview): string | null {
  const days = overview.days_remaining;
  if (days === null) return 'unavailable';
  if (
    days < 0 ||
    overview.subscription_status === 'expired' ||
    overview.subscription_status === 'cancelled'
  ) {
    return 'expired';
  }
  if (days === 0) return 'today';
  return 'left';
}
